<script setup lang="ts">
import { ref, toRefs } from "vue";
import { useApiClient } from "@/api/client";
import { debounce } from "vue-debounce";
import { saveAs } from "file-saver";
import dayjs from "dayjs";
import useToast from "@/ui/composables/useToast";
import ExportScheduleDO = App.Data.ExportScheduler.ExportScheduleDO;
import ExportScheduleExecutionDO = App.Data.ExportScheduler.ExportScheduleExecutionDO;
import ExportScheduleExecutionRecipientDO = App.Data.ExportScheduler.ExportScheduleExecutionRecipientDO;

const props = defineProps<{
	exportSchedule: ExportScheduleDO;
}>();

const { exportSchedule } = toRefs(props);

const itemsPerPage = ref(100);
const items = ref<ExportScheduleExecutionDO[]>([]);
const isLoading = ref(false);
const totalItemCount = ref(0);
const page = ref(1);
const sortBy = ref([{ key: "created_at", order: "desc" }]);

const headers = [
	{ title: "Executed at", key: "created_at", sortable: true },
	{ title: "Message", key: "message", sortable: false },
	{ title: "Recipients", key: "recipients_count", sortable: false },
];

const recipientHeaders = [
	{ title: "Recipient", key: "recipient.email", sortable: false },
	{ title: "File", key: "file", sortable: false, align: "end" as const },
];

const loadItems = debounce(async ({ page, itemsPerPage, sortBy }) => {
	isLoading.value = true;

	const client = useApiClient("exportSchedules");

	const response = await client.getExecutions(
		{ page, itemsPerPage, sortBy },
		exportSchedule.value
	);
	if (response.isOk()) {
		items.value = response.value.data;
		totalItemCount.value = response.value.total;
	}

	isLoading.value = false;
}, 300);

const fileName = (path: string | null) => path?.split("/").pop() ?? null;

const downloadExport = async (
	recipient: ExportScheduleExecutionRecipientDO
) => {
	const toast = useToast();
	const id = toast.loading("Preparing export...", { dismissible: true });

	const response = await useApiClient("exportSchedules").downloadExport(
		recipient
	);

	toast.dismiss(id);

	if (response.isErr()) {
		toast.error("Sorry something went wrong...", { timeout: 5000 });
		return;
	}

	saveAs(response.value, fileName(recipient.exported_file) ?? "export");
	toast.success("Download successful.", { timeout: 5000 });
};
</script>

<template>
	<div class="pa-5">
		<v-data-table-server
			v-model:items-per-page="itemsPerPage"
			v-model:page="page"
			v-model:sort-by="sortBy"
			:headers="headers"
			:items="items"
			:items-length="totalItemCount"
			:loading="isLoading"
			@update:options="loadItems"
			:show-expand="exportSchedule.export_type.delivery_channel === 'email'"
		>
			<template v-slot:item.created_at="{ item }">
				{{ dayjs(item.created_at).format("DD MMM YYYY HH:mm") }}
			</template>
			<template v-slot:item.recipients_count="{ item }">
				{{ item.recipients.length }}
			</template>
			<template v-slot:item.message="{ item }">
				<template v-if="item.has_error_occurred">
					<span class="text-error">{{ item.error_message }}</span>
				</template>
				<template v-else> {{ item.message }} </template>
			</template>
			<template v-slot:expanded-row="{ columns, item }">
				<tr>
					<td :colspan="columns.length" class="pa-0">
						<v-data-table
							class="recipients-table"
							density="compact"
							hide-default-footer
							:items="item.recipients"
							:headers="recipientHeaders"
							:items-per-page="-1"
						>
							<template v-slot:[`item.file`]="{ item: recipient }">
								<v-btn
									variant="text"
									size="20"
									:disabled="!recipient.exported_file"
									v-tooltip="
										recipient.exported_file
											? `Download ${fileName(recipient.exported_file)}`
											: 'No file available'
									"
									@click="downloadExport(recipient)"
								>
									<v-icon size="18">fa: fa-light fa-file-arrow-down</v-icon>
								</v-btn>
							</template>
						</v-data-table>
					</td>
				</tr>
			</template>
		</v-data-table-server>
	</div>
</template>

<style scoped lang="scss">
.recipients-table {
	:deep(tbody tr:nth-child(odd)) {
		background-color: #f5f5f5;
	}

	:deep(tbody tr:nth-child(even)) {
		background-color: #ffffff;
	}
}
</style>
